import React, { useState } from "react";
import "./styles/FAQ.css";

const FAQ = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedItems, setExpandedItems] = useState({});

  const faqSections = [
    {
      title: "General",
      icon: "fa-solid fa-lightbulb",
      questions: [
        {
          q: "What is an eSIM?",
          a: "An eSIM also known as Embedded SIM is a digital SIM card that allows you to activate a cellular plan without using a physical SIM card. It's built into your device, so there's no need to insert or swap physical cards.",
        },
        {
          q: "What is RoamBis?",
          a: "RoamBis is your gateway to fast, reliable, and affordable international data through travel-ready eSIMs. We cover 200+ countries and regions.",
        },
        {
          q: "Can I use my RoamBis eSIM internationally?",
          a: "Yes you can use your RoamBis eSIM internationally. It allows you to switch to local networks and access cellular services without needing to change physical SIM cards when traveling internationally. At RoamBis we offer many international data plans and we also have global data plans.",
        },
        {
          q: "What is the difference between eSIM and a physical SIM?",
          a: "A physical SIM is a removable card that stores your mobile plan data, while an eSIM is a digital SIM built into your device. Both serve the same purpose, but eSIMs offer more flexibility, such as easier switching between carriers and multiple profiles.",
        },
      ],
    },
    {
      title: "Device Compatibility",
      icon: "fa-solid fa-mobile-screen",
      questions: [
        {
          q: "What devices support RoamBis eSIM?",
          a: "eSIM technology is supported on many newer smartphones, tablets, laptops, and smartwatches. Popular devices include the latest iPhones, Google Pixel phones, Samsung Galaxy devices, and more. Kindly check our compatibility page for a full list of supported devices.",
        },
        {
          q: "Which phones are compatible RoamBis Mobile eSIM?",
          a: "The below devices support eSIM installation via automatic online activation. If your device is not listed below, unfortunately it will not work with our eSIM packages. Kindly note that your device must be unlocked and free of any carrier restrictions in order to install eSIM data plans! For smart watches, tablets, and laptops, see our complete list of compatible devices.",
        },
        {
          q: "Can I use an RoamBis eSIM on multiple devices?",
          a: "No, an eSIM can only be activated on one device at a time. However, if you have other devices that need mobile data, you can either turn on Mobile Hotspot / Internet Sharing on your phone (if supported in your country), and connect with your other devices using the broadcasted Wi-Fi network or you can purchase an additional eSIM plan for your extra devices.",
        },
        {
          q: "Can I have both an eSIM and a physical SIM in my device?",
          a: "Many modern devices support dual SIM functionality, allowing you to use both an eSIM and a physical SIM at the same time. You can manage which line is used for calls, texts, and data.",
        },
      ],
    },
    {
      title: "Activation & Usage",
      icon: "fa-solid fa-rocket",
      questions: [
        {
          q: "How do I activate my RoamBis eSIM?",
          a: "To activate your RoamBis eSIM, simply scan the QR code provided by us or follow the setup instructions in your device's settings. Activation steps may vary depending on the device, and location but we will be with you every step of the way.",
        },
        {
          q: "Do I need an internet connection to activate eSIM?",
          a: "Yes, an internet connection (Wi-Fi or cellular data) is required to download the eSIM profile to your device.",
        },
        {
          q: "Will I lose my phone number when I switch to eSIM?",
          a: "No, your phone number will remain the same when switching from a physical SIM to an eSIM.",
        },
        {
          q: "How do I manage multiple eSIM profiles on my device?",
          a: "Some devices allow you to store multiple eSIM profiles. You can manage these profiles through your device's settings, enabling you to switch between networks as needed.",
        },
        {
          q: "How do I purchase and activate an eSIM?",
          a: "Choose a plan for your destination, receive a QR code by email, and scan it with your device. Activation usually takes under 20 seconds.",
        },
      ],
    },
    {
      title: "Plans & Billing",
      icon: "fa-solid fa-credit-card",
      questions: [
        {
          q: "Can I use RoamBis eSIM with prepaid plans?",
          a: "Yes, we offer many prepaid plans in different locations, just like physical SIM cards. You can choose a plan that fits your needs and activate it using an eSIM.",
        },
        {
          q: "How can I check my eSIM balance and usage?",
          a: "You can check your eSIM balance and data usage by logging into your account on our website.",
        },
      ],
    },
    {
      title: "Technical Support",
      icon: "fa-solid fa-tools",
      questions: [
        {
          q: "What should I do if I need to reinstall my eSIM?",
          a: "If you need to reinstall your eSIM (for example, after resetting your device), you can follow the reactivation instructions. You may be able to use the original QR code or activation details.",
        },
        {
          q: "What should I do if my eSIM is not working?",
          a: "If you're having issues with your eSIM, try restarting your device, checking for software updates, or ensuring that your eSIM profile is correctly activated. If the issue persists, contact customer support for further assistance.",
        },
        {
          q: "Can I transfer my eSIM to a different phone?",
          a: "Yes, you can transfer your eSIM to another compatible phone. The process typically involves deactivating the eSIM from the old phone and activating it on the new one. Contact us for assistance.",
        },
        {
          q: "How do I remove or deactivate my eSIM?",
          a: "You can remove or deactivate your eSIM from the settings menu on your device. If you're changing carriers or need to cancel your plan, it's recommended to contact your provider for assistance.",
        },
      ],
    },
    {
      title: "Security & Privacy",
      icon: "fa-solid fa-shield-alt",
      questions: [
        {
          q: "Is eSIM secure?",
          a: "Yes, eSIM technology is designed with enhanced security features. It's built to prevent unauthorized access and SIM swapping, making it a secure option for mobile communication.",
        },
        {
          q: "What happens if I lose my phone or it gets stolen with an eSIM?",
          a: "Contact us immediately to disable your eSIM. You can also lock or wipe your device remotely using your device's built-in security features, such as Find My iPhone or Find My Device.",
        },
        {
          q: "Does RoamBis Mobile record your location or internet activity?",
          a: "We do not record your device location or web browsing activity. Your browsing activity is completely private. As required by law, we keep records containing only the time, data (Megabytes) consumed, and cellular provider of each data connection.",
        },
      ],
    },
  ];

  // Filter sections based on search term (case-insensitive)
  const filteredSections = faqSections.map((section) => ({
    ...section,
    questions: section.questions.filter((q) =>
      q.q.toLowerCase().includes(searchTerm.toLowerCase())
    ),
  }));

  // Toggle expand/collapse state by question
  const toggleQuestion = (sectionIndex, questionIndex) => {
    setExpandedItems((prev) => ({
      ...prev,
      [`${sectionIndex}-${questionIndex}`]:
        !prev[`${sectionIndex}-${questionIndex}`],
    }));
  };

  return (
    <div className="faq-container">
      <div className="faq-content">
        <h1>Frequently Asked Questions</h1>
        <p className="faq-intro">
          Find answers to common questions about RoamBis eSIM services, device
          compatibility, activation process, and more.
        </p>
        <div className="faq-search">
          <div className="search-bar">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search FAQ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="faq-categories">
          {filteredSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="faq-category">
              <div className="category-header">
                <i className={section.icon}></i>
                <h2>{section.title}</h2>
              </div>
              <div className="faq-items">
                {section.questions.map((item, questionIndex) => (
                  <div
                    key={questionIndex}
                    className={`faq-item ${
                      expandedItems[`${sectionIndex}-${questionIndex}`]
                        ? "expanded"
                        : ""
                    }`}
                    onClick={() => toggleQuestion(sectionIndex, questionIndex)}
                  >
                    <div className="faq-question">
                      <h3>{item.q}</h3>
                      <i
                        className={`fas ${
                          expandedItems[`${sectionIndex}-${questionIndex}`]
                            ? "fa-chevron-up"
                            : "fa-chevron-down"
                        }`}
                      ></i>
                    </div>
                    <div className="faq-answer">
                      <p>{item.a}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="faq-contact">
          <h3>Still have questions?</h3>
          <p>We're here to help! Reach out to our support team.</p>
          <div className="contact-buttons">
            <a
              href="mailto:support@roambis.com"
              className="contact-btn email-btn"
            >
              <i className="fas fa-envelope"></i>
              Email Support
            </a>
            <a
              href="https://wa.me/1234567890"
              className="contact-btn whatsapp-btn"
            >
              <i className="fab fa-whatsapp"></i>
              WhatsApp Support
            </a>
          </div>

        </div>
      </div>
    </div>
  );
};

export default FAQ;
